import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, Edit2, Trash2, Users, Eye, FileText, ChevronRight, ClipboardCheck } from '../../components/icons';
import { useAuth } from '../../contexts/AuthContext';
import { LoadingSpinner } from '../../components/LoadingSpinner';
import { fetchPrograms, deleteProgram, updateProgram } from '../../lib/programs';
import type { ProgramWithStats } from '../../types/program';

interface Props {
  creatorType: 'coach' | 'partner';
  basePath: string;
}

export function ProgramsList({ creatorType, basePath }: Props) {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [programs, setPrograms] = useState<ProgramWithStats[]>([]);
  const [deletingId, setDeletingId] = useState<string | null>(null);
  const [togglingId, setTogglingId] = useState<string | null>(null);

  useEffect(() => {
    if (!user) { navigate('/login'); return; }
    loadData();
  }, [user, navigate]);

  const loadData = async () => {
    if (!user?.id) return;
    try {
      const data = await fetchPrograms(user.id);
      setPrograms(data);
    } catch (err) {
      console.error('Error loading programs:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this program? This cannot be undone.')) return;
    setDeletingId(id);
    try {
      await deleteProgram(id);
      setPrograms(prev => prev.filter(p => p.id !== id));
    } catch (err) {
      console.error('Error deleting program:', err);
    } finally {
      setDeletingId(null);
    }
  };

  const handleTogglePublish = async (program: ProgramWithStats) => {
    setTogglingId(program.id);
    try {
      await updateProgram(program.id, { isPublished: !program.isPublished });
      setPrograms(prev => prev.map(p =>
        p.id === program.id ? { ...p, isPublished: !p.isPublished } : p
      ));
    } catch (err) {
      console.error('Error toggling publish:', err);
    } finally {
      setTogglingId(null);
    }
  };

  if (isLoading) return <LoadingSpinner message="Loading programs..." />;

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div>
              <h1 className="freud-heading-lg text-chocolate-900">Programs</h1>
              <p className="text-sm text-chocolate-500">Create and assign structured activity sequences</p>
            </div>
          </div>
          <button
            onClick={() => navigate(`${basePath}/new`)}
            className="freud-btn-primary inline-flex items-center gap-2"
          >
            <Plus className="w-4 h-4" />
            New Program
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {programs.length === 0 ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="freud-card-elevated p-12 text-center"
          >
            <div className="w-16 h-16 bg-sage-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
              <ClipboardCheck className="w-8 h-8 text-sage-600" />
            </div>
            <h2 className="freud-heading-xl text-chocolate-900 mb-3">No Programs Yet</h2>
            <p className="freud-text-base text-chocolate-500 mb-8 max-w-md mx-auto">
              Create your first program to assign structured sequences of activities to your {creatorType === 'coach' ? 'clients' : 'coaches'}.
            </p>
            <button
              onClick={() => navigate(`${basePath}/new`)}
              className="freud-btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              Create Your First Program
            </button>
          </motion.div>
        ) : (
          <div className="grid gap-4">
            {programs.map((program, index) => (
              <motion.div
                key={program.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.05 }}
                className="freud-card-elevated p-6"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <h3 className="freud-heading-base text-chocolate-900 truncate">
                        {program.title || 'Untitled Program'}
                      </h3>
                      <span className={`text-xs px-2.5 py-0.5 rounded-full font-medium ${
                        program.isPublished
                          ? 'bg-success-50 text-green-700'
                          : 'bg-cream-200 text-chocolate-500'
                      }`}>
                        {program.isPublished ? 'Published' : 'Draft'}
                      </span>
                      {program.isTemplate && (
                        <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-sage-100 text-sage-700">
                          Template
                        </span>
                      )}
                      {program.isSequential && (
                        <span className="text-xs px-2.5 py-0.5 rounded-full font-medium bg-blue-50 text-blue-700">
                          Sequential
                        </span>
                      )}
                    </div>
                    {program.description && (
                      <p className="text-sm text-chocolate-500 mb-3 line-clamp-2">{program.description}</p>
                    )}
                    <div className="flex items-center gap-4 text-xs text-chocolate-400">
                      <span className="flex items-center gap-1">
                        <FileText className="w-3.5 h-3.5" />
                        {program.activityCount} {program.activityCount === 1 ? 'activity' : 'activities'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users className="w-3.5 h-3.5" />
                        {program.assignmentCount} {program.assignmentCount === 1 ? 'assigned' : 'assigned'}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleTogglePublish(program)}
                      disabled={togglingId === program.id}
                      className={`p-2 rounded-lg transition-colors ${
                        program.isPublished
                          ? 'text-green-600 hover:bg-green-50'
                          : 'text-chocolate-400 hover:bg-cream-100'
                      }`}
                      title={program.isPublished ? 'Unpublish' : 'Publish'}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    <Link
                      to={`${basePath}/${program.id}/edit`}
                      className="p-2 text-chocolate-400 hover:bg-cream-100 rounded-lg transition-colors"
                      title="Edit"
                    >
                      <Edit2 className="w-4 h-4" />
                    </Link>
                    <button
                      onClick={() => handleDelete(program.id)}
                      disabled={deletingId === program.id}
                      className="p-2 text-chocolate-400 hover:bg-warm-50 hover:text-warm-600 rounded-lg transition-colors"
                      title="Delete"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                    <Link
                      to={`${basePath}/${program.id}/progress`}
                      className="p-2 text-chocolate-400 hover:bg-cream-100 rounded-lg transition-colors"
                      title="View Progress"
                    >
                      <ChevronRight className="w-4 h-4" />
                    </Link>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
